import React, { FormEvent, useContext, useEffect, useState } from "react";
import { SCREEN_PATHS } from "../../../constants";
import { Box, Button, Checkbox, Theme, Typography } from "@mui/material";
import { FormDialog } from "../../shared/FormDialog";
import { ProbateField } from "../../../models/ProbateField";
import { RenderEditableField } from "./RenderEditableField";
import {
  ProbateDetails,
  ProbateFieldCategory,
} from "../../../models/ProbateDetails";
import { useNavigate } from "react-router";
import { ScreenContentWrapper } from "../../shared/ScreenContentWrapper";
import { probateApi } from "../../../apis";
import { useProbateContext } from "../../../ProbateContextWrapper";
import { Loading } from "../../shared/Loading";
import { useAuthContext } from "AuthContextWrapper"

export const ProbateReviewScreen = (): JSX.Element => {
  const navigate = useNavigate();
  const { activeEstate, user } = useAuthContext()
  const { saveFieldAnsweredInLocalStorage, getFieldsAnsweredFromLocalStorage, clearFieldsAnswered } = useProbateContext()
  const [errors, setErrors] = useState<string | string[]>();
  const [confirmationError, setConfirmationError] = useState(false);

  const [incompleteFields, setIncompleteFields] = useState<ProbateField[]>();
  const [probateDetails, setProbateDetails] = useState<ProbateDetails>();
  const [questionComponent, setQuestionComponent] = useState<JSX.Element>();

  useEffect(() => {
    getProbateDetails();
  }, []);

  useEffect(() => {
    // check completeness when probate details changes so that the UI updates to highlight incomplete
    // questions and disable the "Get my packet" button if there are any incomplete
    checkCompleteness();
  }, [probateDetails]);

  const getProbateDetails = async (): Promise<void> => {
    if (activeEstate?.id && user?.id) {
      const fieldsAnswered = getFieldsAnsweredFromLocalStorage();
      let fieldsString = "fields=";
      fieldsAnswered?.forEach((field: ProbateField, index: number) => {
        return (fieldsString += index > 0 ? `,${field?.id}` : field?.id);
      });
      const probateDetails = await probateApi.getProbateDetails(
        activeEstate.id,
        user?.id,
        fieldsString
      );
      try {
        probateDetails &&
          probateDetails.injectPreviouslyAnswered(fieldsAnswered);
        setProbateDetails(probateDetails);
      } catch (e) {
        // if injectPreviouslyAnswered failed for any reason clear it from localStorage so we don't get stuck w/bad data
        clearFieldsAnswered?.();
        setProbateDetails(probateDetails);
      }
    }
  };

  const checkCompleteness = (): ProbateField[] | undefined => {
    const init: ProbateField[] = [];
    const fields = probateDetails?.fieldsUsed?.reduce(
      (fields: ProbateField[], field: ProbateField) => {
        const haveAnswer = field.answerValue !== undefined;
        if (haveAnswer || field?.isComputed) {
          // if we have an answer or the field is computer on the backend don't add
          return fields;
        } else {
          // add to incomplete array
          return [...fields, field];
        }
      },
      init
    );
    // highlight incomplete fields
    setIncompleteFields(fields);
    return fields;
  };

  const handleSubmit = (event?: FormEvent<HTMLFormElement>) => {
    event?.preventDefault();
    const formErrors: string[] = [];
    const fields = checkCompleteness();
    const legalCheckbox = window.document.getElementsByName(
      "confirmationCheckbox"
    )[0] as HTMLInputElement;
    if (!legalCheckbox?.checked) {
      formErrors.push(
        "Please confirm that the answers provided are accurate to the best of your knowledge."
      );
      setConfirmationError(true);
    }
    if (fields?.length) {
      // display error message near continue and highlight fields
      formErrors.push(
        "Please provide and review all of the required information before continuing."
      );
    }
    if (formErrors?.length > 0) {
      setErrors(formErrors);
    } else {
      // go to animation screen
      navigate(SCREEN_PATHS.probateAssemble);
    }
  };

  const getCategoryCards = (
    categoriesWithFields?: ProbateFieldCategory[]
  ): JSX.Element[] | JSX.Element | undefined => {
    return categoriesWithFields?.map((category: ProbateFieldCategory) => {
      const title = category?.title;
      return category?.fields ? (
        <React.Fragment key={category.id}>
          <Typography sx={{ mt: 4, mb: 1 }}>{title}</Typography>
          {getCategoryCard(title, category.fields)}
        </React.Fragment>
      ) : (
        <React.Fragment key={category.id} />
      );
    });
  };

  const getCategoryCard = (
    title: string,
    fields: ProbateField[]
  ): JSX.Element => {
    return (
      <Box
        sx={{
          my: 2,
          p: 2,
          backgroundColor: (theme: Theme) => theme.palette.background.paper,
        }}
      >
        {fields?.map((field: ProbateField, index: number) => {
          const highlight = !!incompleteFields?.find(
            (incompleteField: ProbateField) => incompleteField.id === field.id
          );
          // console.log("field?.id", field?.id);
          return !field?.isComputed ? (
            <RenderEditableField
              first={index === 0}
              key={field?.id}
              field={field}
              highlight={highlight}
              setQuestionComponent={(component?: JSX.Element) => {
                setErrors(undefined);
                // displays question in modal
                setQuestionComponent(component);
              }}
              onAnswer={async (answer: string | number | boolean) => {
                try {
                  if(user?.id) {
                    field.answerValue = answer;
                    await probateApi.saveFieldToKnowledgeEngine(user?.id, field);
                    saveFieldAnsweredInLocalStorage(field);
                  }
                } catch (error) {
                  console.error(error);
                  setErrors(
                    "Sorry, saving your answer failed.  Please try again"
                  );
                }
                // hide modal
                setQuestionComponent(undefined);
                // refresh probate details
                await getProbateDetails();
                // refresh completeness
                checkCompleteness();
              }}
            />
          ) : null;
        })}
      </Box>
    );
  };

  // console.log("fieldsAnswered: ", fieldsAnswered);
  // console.log('-- probateDetails=', JSON.stringify(probateDetails, null, 2));
  return (
    <ScreenContentWrapper
      pageSuffix="Probate"
      title="Get your Probate packet"
      step={8}
      totalSteps={10}
      showBeta={true}
    >
      {!probateDetails ? (
        <Loading />
      ) : (
        <>
          <Typography variant="h5" sx={{ my: 3 }}>
            Before we show you your Probate Packet, let’s confirm everything
            you’ve told us so far
          </Typography>
          {getCategoryCards(probateDetails?.categoriesWithFields)}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mt: 4,
            }}
          >
            <form onSubmit={handleSubmit}>
              <Box
                sx={{
                  textAlign: "center",
                }}
              >
                <label>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-around",
                      mb: 2,
                      alignItems: "flex-start",
                    }}
                  >
                    <Checkbox
                      name="confirmationCheckbox"
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>
                      ) => {
                        setErrors(undefined);
                        setConfirmationError(!event.target.checked);
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ textAlign: "center", pt: 1 }}
                      color={confirmationError ? "error" : "default"}
                    >
                      I am confirming that the answers to the questions provided
                      above are accurate to the best of my knowledge and affirm
                      that Atticus is not providing any form of legal advice or
                      services.
                    </Typography>
                  </Box>
                </label>
                {errors &&
                  (errors.constructor === Array
                    ? errors?.map((error: string) => {
                      return (
                        <Typography color="error" sx={{ my: 2 }} key={error}>
                          {error}
                        </Typography>
                      );
                    })
                    : errors)}
                <Button
                  variant="contained"
                  type="submit"
                  disabled={incompleteFields && incompleteFields.length > 0}
                >
                  Get my packet
                </Button>
              </Box>
            </form>
          </Box>
          {/* Modal window to display the field question form */}
          <FormDialog
            open={questionComponent !== undefined}
            content={questionComponent}
            confirmText="Save"
            onConfirm={() => setQuestionComponent(undefined)}
          />
        </>
      )}
    </ScreenContentWrapper>
  );
};
